import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import MovieCard from '@/components/feature/MovieCard';
import FilterBar, { type FilterState } from './components/FilterBar';
import { movies } from '@/mocks/content';
import type { ContentItem, ContentType } from '@/types/content';
import { useSeo } from '@/hooks/useSeo';

const VALID_TYPES: ContentType[] = ['电影', '电视剧', '综艺'];

const TYPE_DESC: Record<string, string> = {
  电影: '汇聚院线大片与经典佳片，覆盖科幻、悬疑、动作、剧情等多种题材，按地区与年代快速定位想看的好片。',
  电视剧: '热门剧集持续更新，涵盖古装、都市、悬疑、年代等类型，追剧不迷路。',
  综艺: '音乐竞技、生活纪实、推理脱口秀一网打尽，轻松愉快的综艺内容随手可看。',
};

const DEFAULT_FILTER: FilterState = {
  region: '全部',
  year: '全部',
  genre: '全部',
  sort: '最新更新',
};

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const decodedType = type ? decodeURIComponent(type) : '';
  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [visibleCount, setVisibleCount] = useState(8);

  const isValid = VALID_TYPES.includes(decodedType as ContentType);
  const currentType = isValid ? (decodedType as ContentType) : '电影';

  useSeo(
    `${currentType}列表 - 91大神影视大全`,
    `${TYPE_DESC[currentType] ?? ''} 91大神影视大全为您提供丰富的${currentType}资源。`,
  );

  const allItems = movies as ContentItem[];

  const filtered = useMemo(() => {
    let list = allItems.filter((item) => item.type === currentType);
    if (filter.region !== '全部') {
      list = list.filter((item) => item.region === filter.region);
    }
    if (filter.year !== '全部') {
      list = list.filter((item) => `${item.year}` === filter.year);
    }
    if (filter.genre !== '全部') {
      list = list.filter((item) => item.genres.includes(filter.genre));
    }
    const sorted = [...list];
    if (filter.sort === '最热播放') {
      sorted.sort((a, b) => b.views - a.views);
    } else if (filter.sort === '评分最高') {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    }
    return sorted;
  }, [allItems, currentType, filter]);

  const visible = filtered.slice(0, visibleCount);

  const handleChange = (key: keyof FilterState, value: string) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    setVisibleCount(8);
  };

  const handleReset = () => {
    setFilter(DEFAULT_FILTER);
    setVisibleCount(8);
  };

  return (
    <div className="min-h-screen w-full bg-background-100 flex flex-col">
      <SiteHeader />

      <main className="w-full flex-1 px-4 md:px-8 py-6 md:py-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-base" />
          <span className="text-foreground-800">{currentType}</span>
        </nav>

        <div className="mb-6">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
            {currentType}列表
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            {TYPE_DESC[currentType]}
          </p>
        </div>

        <div className="mb-6">
          <FilterBar state={filter} onChange={handleChange} onReset={handleReset} />
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-foreground-600">
            共找到 <span className="text-accent-600 font-semibold">{filtered.length}</span> 部内容
          </p>
          <p className="text-sm text-foreground-500">排序：{filter.sort}</p>
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 flex flex-col items-center text-center">
            <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-400">
              <i className="ri-inbox-archive-line text-3xl" />
            </span>
            <h2 className="mt-5 font-heading font-semibold text-lg text-foreground-900">
              没有找到符合条件的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-500 max-w-md">
              试试更换筛选条件，或重置筛选查看该分类下的全部影视。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-5 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
              {visible.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>

            {visibleCount < filtered.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + 8)}
                  className="flex items-center gap-2 px-8 py-3 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line text-lg" />
                  加载更多（{filtered.length - visibleCount}）
                </button>
              </div>
            )}

            {visibleCount >= filtered.length && filtered.length > 8 && (
              <p className="mt-8 text-center text-sm text-foreground-500">已经到底啦</p>
            )}
          </>
        )}
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}